import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Transition,
} from '@headlessui/react'
import { tm } from '@captaincause/ui/tm'
import { Icon } from '@captaincause/ui/Icon'
import { Button } from 'components/Button'
import LogoBlack from 'assets/dift-logo-black.webp'
import { NavPopover } from './Popover'
import { NavHamburger } from './Hamburger'
import { NavCallToActions } from './CallToActions'
import { NAV } from './items'
import type { Localization } from '@captaincause/types'
import type { Item as ItemType } from './items'

interface ItemProps {
  item: ItemType
  small?: boolean
}

function Item({ item, small = false }: ItemProps) {
  const { t } = useTranslation('nav.component')

  return (
    <Button
      key={item.title}
      href={item.url}
      variant="raw"
      className="w-full rounded-md hover:bg-light-beige focus-visible:bg-light-beige md:w-fit"
      innerClassName="w-full md:w-fit "
      locale={item.locale}
    >
      <div className="flex items-start p-2 transition-opacity duration-300 ease-out">
        <div
          className={tm('h-fit w-fit rounded-2xl p-2', item.illustrationStyle)}
        >
          <Image
            src={item.illustration}
            alt={`Illustration about ${t(item.title)}`}
            className={tm(
              'block h-10 w-10 max-w-[40px]',
              small && 'h-8 w-8 max-w-[32px]',
            )}
          />
        </div>
        <div
          className={tm(
            'ml-4 flex w-fit min-w-[150px] max-w-[190px] flex-col font-medium',
            small && 'w-full max-w-[unset]',
          )}
        >
          <div className="mb-1">{t(item.title)}</div>
          {item.description ? (
            <div className="w-full whitespace-normal text-xs text-dark-gray md:max-w-[180px]">
              {t(item.description)}
            </div>
          ) : null}
        </div>
      </div>
    </Button>
  )
}

interface NavProps {
  localizations: Localization[]
}

export function Nav({ localizations }: NavProps) {
  const { t } = useTranslation('nav.component')

  return (
    <header
      id="header"
      className="sticky top-0 z-50 h-auto w-full transform-gpu border-b border-inside bg-light-beige duration-300 ease-out"
    >
      <div className="mx-auto flex max-w-screen-2xl flex-col flex-wrap items-center justify-center gap-x-1 gap-y-2 px-4 py-2 md:flex-row md:px-14 md:py-4 xl:flex-nowrap xl:justify-between">
        {/* Desktop */}
        <div className="hidden md:flex md:space-x-8 xl:mb-0 xl:space-x-14">
          <Button href="/" variant="raw">
            <Image
              src={LogoBlack}
              alt="Dift logo"
              priority
              className="block h-auto w-20 min-w-[80px]"
            />
          </Button>
          <div className="flex items-center justify-start gap-x-4">
            {NAV.map(nav => (
              <NavPopover key={nav.title} label={t(nav.title)}>
                {nav.items.map(item => (
                  <Item key={item.title} item={item} />
                ))}
              </NavPopover>
            ))}
          </div>
        </div>
        <div className="hidden items-center space-x-2 md:flex xl:p-0">
          <NavCallToActions localizations={localizations} />
        </div>

        {/* Mobile */}
        <div className="flex w-full items-center justify-between md:hidden">
          <Button href="/" variant="raw">
            <Image
              src={LogoBlack}
              alt="Dift logo"
              className="block h-auto w-10 min-w-[64px]"
              priority
            />
          </Button>

          <div>
            <NavHamburger localizations={localizations}>
              {NAV.map(nav => (
                <Disclosure
                  key={`hamburger-${nav.title}`}
                  defaultOpen={t(nav.title) === 'Le dift'}
                >
                  {({ open }) => (
                    <>
                      <DisclosureButton className="flex items-center justify-between border-t border-inside py-4 text-left text-lg first:border-none">
                        {t(nav.title)}
                        <Icon
                          icon="chevron"
                          className={tm(
                            'ml-1 transform-gpu transition-transform duration-300 ease-out',
                            open && 'rotate-180',
                          )}
                        />
                      </DisclosureButton>
                      <Transition
                        as="div"
                        className="transition-opacity ease-out data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-100"
                      >
                        <DisclosurePanel className="mb-4 flex flex-col space-y-6">
                          {nav.items.map(item => (
                            <Item key={item.title} item={item} small />
                          ))}
                        </DisclosurePanel>
                      </Transition>
                    </>
                  )}
                </Disclosure>
              ))}
            </NavHamburger>
          </div>
        </div>
      </div>
    </header>
  )
}
